import { useNavigate } from 'react-router-dom'
import type { TreatmentListItem } from '../../store/treatmentListSlice'

interface TreatmentListTableProps {
  treatments: TreatmentListItem[]
}

const statusStyles: Record<string, string> = {
  Planned: 'bg-[#e0eaf0] text-status-info',
  'In Progress': 'bg-[#deeaf5] text-[#3a6a8a]',
  Completed: 'bg-accent-subtle-bg text-status-success',
  Cancelled: 'bg-[#e8e6e2] text-text-muted',
}

function formatTxNumber(num: number): string {
  return `TX-${String(num).padStart(4, '0')}`
}

function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function TreatmentListTable({ treatments }: TreatmentListTableProps) {
  const navigate = useNavigate()

  if (treatments.length === 0) {
    return (
      <div data-testid="treatments-table" className="flex flex-col">
        <p className="text-[12px] text-text-muted py-8 text-center">No treatments found</p>
      </div>
    )
  }

  return (
    <div data-testid="treatments-table" className="flex flex-col">
      {/* Header row */}
      <div
        className="tx-table-header grid items-center h-9 text-[11px] font-medium text-text-muted uppercase tracking-wide"
        style={{
          gridTemplateColumns: '90px 1fr 80px 120px 1fr 1fr 110px 100px',
        }}
      >
        <span className="px-2">Treatment ID</span>
        <span className="px-2">Patient Name</span>
        <span className="px-2">Species</span>
        <span className="px-2">Treatment Type</span>
        <span className="px-2">Diagnosis</span>
        <span className="px-2">Assigned Vet</span>
        <span className="px-2">Status</span>
        <span className="px-2">Date</span>
      </div>

      {/* Data rows */}
      {treatments.map((tx) => (
        <div
          key={tx.id}
          data-testid={`treatment-row-${tx.id}`}
          onClick={() => navigate(`/treatments/${tx.id}`)}
          className="tx-table-row grid items-center h-[44px] text-[13px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
          style={{
            gridTemplateColumns: '90px 1fr 80px 120px 1fr 1fr 110px 100px',
          }}
        >
          <span className="px-2 text-[13px] font-[450] text-text-muted" data-testid="treatment-id">
            {formatTxNumber(tx.treatment_number)}
          </span>
          <span className="px-2 font-[500] truncate" data-testid="treatment-patient-name">
            {tx.patient_name}
          </span>
          <span className="px-2 text-[12px] text-text-secondary" data-testid="treatment-species">
            {tx.patient_species}
          </span>
          <span className="px-2 text-[12px] text-text-secondary truncate" data-testid="treatment-type">
            {tx.treatment_type}
          </span>
          <span className="px-2 text-[12px] text-text-secondary truncate" data-testid="treatment-diagnosis">
            {tx.diagnosis}
          </span>
          <span className="px-2 text-[12px] text-text-secondary truncate" data-testid="treatment-vet">
            {tx.veterinarian_name || '-'}
          </span>
          <span className="px-2" data-testid="treatment-status">
            <span
              className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${statusStyles[tx.status] || 'bg-[#e8e6e2] text-text-muted'}`}
            >
              {tx.status}
            </span>
          </span>
          <span className="px-2 text-[12px] text-text-muted" data-testid="treatment-date">
            {formatDate(tx.treatment_date)}
          </span>
        </div>
      ))}
    </div>
  )
}
